import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, User, Clock, Pin, Plus, ChevronRight, Home, HelpCircle } from 'lucide-react';

import { useCategoriesByActivity } from '@/hooks/useCategoriesByActivity';
import { useCategoryById, useCategoryBySlug } from '@/hooks/useCategories';
import { useTopicsLegacy as useTopics } from '@/hooks/useTopicsLegacy';
import { useAuth } from '@/hooks/useAuth';
import { useCategoryStats } from '@/hooks/useCategoryStats';
import { formatDistanceToNow } from 'date-fns';
import { QuickTopicModal } from './QuickTopicModal';
import { CategoryRequestModal } from './CategoryRequestModal';
import { AdminControls } from './AdminControls';
import { TopicTable } from './TopicTable';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Helper function to build breadcrumb hierarchy
const buildBreadcrumbHierarchy = (category: any) => {
  console.log('Building breadcrumb hierarchy for category:', category);
  const breadcrumbs = [];
  let current = category;
  
  // Build breadcrumb hierarchy by traversing parent_category chain
  while (current) {
    console.log('Processing breadcrumb level:', current.name, 'with parent:', current.parent_category);
    breadcrumbs.unshift({
      name: current.name,
      slug: current.slug,
      id: current.id
    });
    
    // Move to parent category
    current = current.parent_category;
  }
  
  console.log('Final breadcrumbs array:', breadcrumbs);
  return breadcrumbs;
};

const SubcategoryCard = ({ subcat }: { subcat: any }) => {
  const { data: stats } = useCategoryStats(subcat.id);
  
  return (
    <Link to={`/${subcat.slug}`}>
      <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: subcat.color }}
            />
            <h3 className="font-semibold text-sm text-gray-900">{subcat.name}</h3>
          </div>
          <div className="flex items-center space-x-2">
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">{subcat.description}</p>
        <div className="flex items-center text-xs text-gray-500 space-x-4">
          <div className="flex items-center space-x-1">
            <MessageSquare className="h-3 w-3" />
            <span>{stats?.topic_count || 0} topics</span>
          </div>
          <div className="flex items-center space-x-1">
            <User className="h-3 w-3" />
            <span>{stats?.post_count || 0} posts</span>
          </div>
          {subcat.last_activity_at && (
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3" />
              <span>{formatDistanceToNow(new Date(subcat.last_activity_at))} ago</span>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
};

export const CategoryView = () => {
  const { categoryId, categorySlug, subcategorySlug } = useParams();
  
  console.log('CategoryView params:', { categoryId, categorySlug, subcategorySlug });
  
  // Handle both legacy UUID routing and new slug routing
  const isLegacyRoute = !!categoryId;
  const { user } = useAuth();
  
  // Check if categoryId is a UUID (proper UUID format: 8-4-4-4-12 characters)
  const isUUID = categoryId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(categoryId);
  
  // Determine which slug to use for the query
  const slugToLookup = subcategorySlug || categorySlug || (!isUUID ? categoryId : '');
  
  console.log('CategoryView logic:', { isUUID, slugToLookup, categoryId });
  
  // Only call hooks with valid parameters to prevent errors
  const { data: categoryBySlug, isLoading: categoryBySlugLoading, error: slugError } = useCategoryBySlug(
    slugToLookup || ''
  );
  const { data: categoryById, isLoading: categoryByIdLoading, error: idError } = useCategoryById(
    (isUUID && categoryId) ? categoryId : ''
  );
  
  // Use the appropriate result based on what we think the categoryId is
  let category, categoryLoading, categoryError;
  
  if (isUUID) {
    console.log('Using categoryById result');
    category = categoryById;
    categoryLoading = categoryByIdLoading;
    categoryError = idError;
  } else {
    console.log('Using categoryBySlug result');
    category = categoryBySlug;
    categoryLoading = categoryBySlugLoading;
    categoryError = slugError;
  }
  
  console.log('Final category data:', category);
  
  const { data: subcategories, isLoading: subcategoriesLoading } = useCategoriesByActivity(category?.id, category?.level ? category.level + 1 : undefined);
  const { data: topics, isLoading: topicsLoading } = useTopics(category?.id);

  if (categoryLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!category && !categoryLoading) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">Category not found</h2>
        <p className="text-gray-600 mt-2">The category "{slugToLookup || categoryId}" doesn't exist.</p>
        <Button asChild className="mt-4">
          <Link to="/">Back to Home</Link>
        </Button>
      </div>
    );
  }

  // Determine if we should show subcategories or topics
  const hasSubcategories = subcategories && subcategories.length > 0;
  const isLevel3Category = category?.level === 3; // Only Level 3 categories can have topics

  return (
    <div className="forum-spacing w-full overflow-x-hidden">
      {/* Breadcrumb */}
      {category && (
        <Breadcrumb className="overflow-x-auto">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">
                  <Home className="h-4 w-4" />
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            
            {buildBreadcrumbHierarchy(category).map((breadcrumb, index, array) => [
              <BreadcrumbSeparator key={`sep-${breadcrumb.id}`} />,
              <BreadcrumbItem key={breadcrumb.id}>
                {index === array.length - 1 ? (
                  <BreadcrumbPage className="max-w-full truncate">{breadcrumb.name}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={`/${breadcrumb.slug}`} className="max-w-full truncate">
                      {breadcrumb.name}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            ]).flat()}
          </BreadcrumbList>
        </Breadcrumb>
      )}

      {/* Category Header */}
      {category && (
        <Card className="p-3 sm:p-4 w-full border border-forum-border-subtle">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: category.color }}
                  />
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 min-w-0 truncate">{category.name}</h1>
                </div>
                <AdminControls 
                  content={category} 
                  contentType="category" 
                  onDelete={() => window.location.href = '/'}
                />
              </div>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">{category.description}</p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                {category.region && <span>Region: {category.region}</span>}
                {category.birth_year && <span>Birth Year: {category.birth_year}</span>}
                {category.play_level && <span>Level: {category.play_level}</span>}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              {/* Show different content based on category level and moderation requirements */}
              {(category.level === 3 || (category.level === 2 && !category.requires_moderation)) ? (
                // Level 3 categories and level 2 categories without moderation allow topic creation
                <>
                  <QuickTopicModal 
                    preselectedCategoryId={category.id}
                    trigger={
                      <Button size="sm" className="w-full sm:w-auto">
                        <Plus className="h-4 w-4 mr-2" />
                        Start Discussion
                      </Button>
                    }
                  />
                  
                  {/* Category request button */}
                  <CategoryRequestModal 
                    currentCategoryId={category.id}
                    trigger={
                      <Button variant="outline" size="sm" className="w-full sm:w-auto">
                        <HelpCircle className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Request Category</span>
                        <span className="sm:hidden">Request</span>
                      </Button>
                    }
                  />
                </>
              ) : (
                // Level 1 categories and level 2 categories requiring moderation are for browsing only
                <div className="flex flex-col items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    Browse Only - Select a {
                      category.slug?.includes('general') ? 'Category' : 
                      category.slug?.includes('tournaments') ? 'Location' :
                      category.slug?.includes('usa') || category.region === 'USA' ? 'State' : 'Province'
                    } to Post
                  </Badge>
                  <CategoryRequestModal 
                    currentCategoryId={category.id}
                    trigger={
                      <Button variant="outline" size="sm" className="w-full sm:w-auto">
                        <HelpCircle className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Request Category</span>
                        <span className="sm:hidden">Request</span>
                      </Button>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      )}


      {/* Subcategories or Topics */}
      {category && hasSubcategories ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Browse Categories</h2>
            <div className="text-xs sm:text-sm text-gray-500">
              Can't find what you're looking for?{' '}
              <CategoryRequestModal 
                currentCategoryId={category.id}
                trigger={
                  <Button variant="link" size="sm" className="p-0 h-auto text-blue-600 text-xs sm:text-sm">
                    Request a new category
                  </Button>
                }
              />
            </div>
          </div>
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full">
            {subcategories.map((subcat) => (
              <SubcategoryCard key={subcat.id} subcat={subcat} />
            ))}
          </div>
        </>
      ) : category ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Topics</h2>
          </div>
          <TopicTable 
            topics={topics || []}
            categorySlug={category?.slug}
            loading={topicsLoading}
          />
        </>
      ) : null}
    </div>
  );
};
